import { useAccount, useConnect, useDisconnect, useChainId, useSwitchChain } from 'wagmi';
import { useCallback, useEffect, useState } from 'react';
import { base } from '../config/wagmi';

export const useCoinbaseWallet = () => {
  const { address, isConnected, connector } = useAccount();
  const { connect, connectors, status, error: connectError } = useConnect();
  const { disconnect } = useDisconnect();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const [error, setError] = useState<string | null>(null);

  // Derive isConnecting from status
  const isConnecting = status === 'pending';

  // Get Coinbase Wallet connector (prioritized)
  const coinbaseConnector = connectors.find(
    (c) => c.id === 'coinbaseWalletSDK' || c.name.toLowerCase().includes('coinbase')
  );

  // Get injected connector as fallback (for Brave, Chrome, etc.)
  const injectedConnector = connectors.find(
    (c) => c.id === 'injected' || c.name.toLowerCase().includes('injected')
  );

  // Handle connection errors
  useEffect(() => {
    if (connectError) {
      setError(connectError.message || 'Failed to connect wallet');
    }
  }, [connectError]);

  // NOTE: We intentionally do NOT auto-switch chains here. The Coinbase Smart
  // Wallet handles chain switching via a popup, and browsers only allow popups
  // inside a user gesture. Calling switchChain from an effect (no gesture) gets
  // the popup blocked → the SDK's "Pop up was blocked" modal. We already request
  // Base at connect time (chainId: base.id below); if the wallet still ends up on
  // the wrong network, the UI surfaces a user-clickable "Switch to Base" button
  // (see WalletConnect), which runs switchToBase inside a real click.

  const connectWallet = useCallback(async () => {
    setError(null);
    try {
      // Try Coinbase Wallet first
      if (coinbaseConnector) {
        connect({ connector: coinbaseConnector, chainId: base.id });
      }
      // Fallback to injected wallet (Brave, MetaMask, etc.)
      else if (injectedConnector) {
        connect({ connector: injectedConnector, chainId: base.id });
      }
      // If no connectors available, show helpful error
      else {
        setError('No wallet detected. Please install Coinbase Wallet or use a Web3-enabled browser.');
      }
    } catch (err: any) {
      console.error('Failed to connect wallet:', err);
      setError(err.message || 'Failed to connect wallet');
    }
  }, [connect, coinbaseConnector, injectedConnector]);

  const disconnectWallet = useCallback(() => {
    setError(null);
    disconnect();
  }, [disconnect]);

  // Get provider from connector. In wagmi v2 getProvider() is async, so resolve
  // it into state rather than holding the unresolved Promise (which is always
  // truthy and never an actual provider).
  const [provider, setProvider] = useState<unknown | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!connector?.getProvider) {
      setProvider(null);
      return;
    }
    connector
      .getProvider()
      .then((p) => {
        if (!cancelled) setProvider(p ?? null);
      })
      .catch(() => {
        if (!cancelled) setProvider(null);
      });
    return () => {
      cancelled = true;
    };
  }, [connector]);

  return {
    isConnected,
    address: address || null,
    provider,
    isConnecting,
    error: error || (connectError ? connectError.message : null),
    connectWallet,
    disconnectWallet,
    chainId,
    isOnBaseNetwork: chainId === base.id,
    switchToBase: () => switchChain?.({ chainId: base.id }),
  };
};